import { atom, atomFamily, selector, selectorFamily, useRecoilCallback, useRecoilValue, waitForAll } from "recoil";
import { savedDisplayOperationState } from "../hooks/useDisplayOperation";
import { _groupBy, _orderBy, getProperty, isEmpty, maxBy } from "../utils/FPEUtils";
import axios from "axios";
import groupIcon from "../images/message/group.svg";
import { userState } from "./UserState";
import { disabledContentsWithID } from "./DisabledContentsState";
import deepmerge from "deepmerge";
import {
  COMMUNICATE_ERROR_MESSAGE_QUEUECHECK,
  COMMUNICATE_ERROR_MESSAGE_READ_GET,
  COMMUNICATE_ERROR_MESSAGE_MESSAGE_DESTINATION,
  COMMUNICATE_ERROR_MESSAGE_MESSAGEGROUP_LIST,
  COMMUNICATE_ERROR_MESSAGE_UPLINK_DELETE,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD,
  COMMUNICATE_ERROR_MESSAGE_UPLINK_THREAD, COMMUNICATE_ERROR_MESSAGE_READ_UPDATE
} from "../constants/ErrorMessage";
import { coPiAlertState } from "./CopilotListState";
import { useDownLinkSound } from "../components/message/hooks/useDownLinkSound";

export const MESSAGE_WIDGET = 'MESSAGE_WIDGET';
export const MESSAGE_TYPE_UPLINK = 'Uplink';
export const MESSAGE_TYPE_DOWNLINK = 'Downlink';

/**
 * コミュニケーションボードの表示On/Off状態
 */
export const messageWidgetVisibleState = atom({
  key: 'messageWidgetVisibleState',
  default: selector({
    key: 'messageWidgetVisibleStateSelector',
    get: ({get}) => {
      const disabled = get(disabledContentsWithID(MESSAGE_WIDGET));
      return disabled ? false : getProperty(get(savedDisplayOperationState), `${MESSAGE_WIDGET}.enable`) || false;
    }
  })
});

/**
 * コミュニケーションボードがDockInかFloatかの状態
 */
export const messageWidgetDockInState = atom({
  key: 'messageWidgetDockInState',
  default: selector({
    key: 'messageWidgetDockInStateSelector',
    get: ({get}) => {
      const value = getProperty(get(savedDisplayOperationState), `${MESSAGE_WIDGET}.dockInMode`);
      return value !== undefined ? value : true;
    }
  })
});

/**
 * コミュニケーションボードがfloatしているときのサイズ
 */
export const messageWidgetSizeState = atom({
  key: 'messageWidgetSizeState',
  default: selector({
    key: 'messageWidgetSizeStateSelector',
    get: ({get}) => {
      return getProperty(get(savedDisplayOperationState), `${MESSAGE_WIDGET}.size`) || {};
    }
  })
});

/**
 * コミュニケーションボードのポジション
 */
export const messageWidgetPositionState = atom({
  key: 'messageWidgetPositionState',
  default: selector({
    key: 'messageWidgetPositionStateSelector',
    get: ({get}) => {
      return getProperty(get(savedDisplayOperationState), `${MESSAGE_WIDGET}.position`) || null;
    }
  })
});

/**
 * コミュニケーションボードのポジション
 */
export const messageWidgetDragPositionState = atom({
  key: 'messageWidgetDragPositionState',
  default: selector({
    key: 'messageWidgetDragPositionStateSelector',
    get: ({get}) => {
      return getProperty(get(savedDisplayOperationState), `${MESSAGE_WIDGET}.dragPosition`) || {x:0, y: 40};
    }
  })
});

/**
 * 全機体のメッセージ一覧.
 */
export const messageListState = atom({
  key: 'messageListState',
  default: []
});

/**
 * 新着メッセージリスト（Uplink/Downlinkどちらも対象）.
 */
export const newMessageListState = atom({
  key: 'newMessageList',
  default: []
});

/**
 * 全機体のメッセージ一覧を更新する関数を取得
 * メッセージの最新upsertTimeを指定し（なければnull）、全体メッセージ
 */
export const messageListUpdater = selectorFamily({
  key: 'messageListUpdater',
  get: userGroupId => async ({get, getCallback}) => {
    //ALLの対象のaircraft(アイコン画像取得用）
    const aircraftList = get(messageAircraftListQuery(userGroupId));

    //ALLの対象のaircraft_code
    const aircraftCodeList = get(messageAircraftIds(userGroupId));

    /**
     * uplink/thread API(メッセージ一覧取得）を叩いてmessageListStateを更新する
     */
    const execute = getCallback(({snapshot, set}) => async () => {
      const latestUpsertTime = await snapshot.getPromise(messageLatestUpsertTimeSelector);
      const release = snapshot.retain();
      return getMessageListQuery(aircraftCodeList, latestUpsertTime).then(async result => {
        if (result.length !== 0) {
          const prevData = await snapshot.getPromise(messageListState);
          const newData = formatMessage(result);

          if (newData.length === 0) {
            return {newData: false, unreadCount: 0}
          }

          // 新着メッセージにアイコンを埋め込む
          newData.forEach(msg => {
            if (msg.from.type === MESSAGE_TYPE_DOWNLINK && !msg.image) {
              const icon = aircraftList.find(aircraft => aircraft.aircraft_code === msg.from.name);
              msg.image = icon?.aircraft_image ?? '';
            }
          });
          // 前回までのデータと新着メッセージをマージ
          const messageList = mergeMessage(prevData, newData);

          //全機体のメッセージ一覧を更新
          set(messageListState, messageList);

          //新着メッセージを更新
          if (prevData.length !== 0) {
            set(newMessageListState, newData);
          }

          // 全宛先の中でメッセージ受信音の設定がONになっている機体を取得
          const alertSetting = await snapshot.getPromise(coPiAlertState);
          const soundAircraftList = aircraftCodeList.filter(aircraftCode => {
            const alertRecord = alertSetting.find(alert=> aircraftCode === alert.aircraft_code);
            return alertRecord?.alertSwitch ?? false
          });

          //対象機体の未読件数を取得
          const unreadCount = snapshot.getLoadable(waitForAll(soundAircraftList.map(aircraftCode => messageUnreadListWithAircraftCode({aircraftCode, messageList: newData})))).contents
            .reduce((totalCount, unReadList) => {
              totalCount += unReadList.length
              return totalCount;
            }, 0);

          return {
            newData: true,
            unreadCount
          };
        }
        return {newData: false, unreadCount: 0}
      }).catch(e => {
        throw e;
      }).finally(() => {
        release();
      });
    });
    return {
      execute
    };
  }
});

/**
 * 全機体のメッセージ取得.
 * パラメータで指定された機体番号（配列）に属する機体のメッセージ一覧を取得する。
 * 取得する範囲はupsertTimeが指定されていない場合、72時間分.
 * upsertTimeが指定されている場合、upsertTime以降.
 */
const getMessageListQuery = (aircraftCodeList, upsertTime) => {
  return new Promise((resolve, reject) => {
    const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
    const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
    axios.get(`${apiUrl}/uplink/thread`, {
      params: {
        aircraft_code: aircraftCodeList.join(','),
        ...(upsertTime !== null && {
          last_upsert_time: upsertTime
        })
      },
      headers: {
        'x-api-key': apiKey
      },
      errorHandle: {
        message: COMMUNICATE_ERROR_MESSAGE_UPLINK_THREAD
      }
    }).then(res => {
      resolve(res.data.threads);
    }).catch(e => {
      console.error('message thread failed.', e);
      reject(e);
    });
  });
};

/**
 * APIから返される機体ごとにまとめられたメッセージ一覧をアプリで扱う形式に変換する.
 */
const formatMessage = aircraftMessageList => {
  //機体毎にまとまっているのmessageをflatな階層にする（その際、messageにaircraft_codeを設定）
  const flatMessages = []
  aircraftMessageList.reduce((accumulator, current) => {
    current.messages.forEach(msg => {
      accumulator.push({
        ...msg,
        aircraftCode: current.aircraft_code
      });
    })
    return accumulator;
  }, flatMessages);
  //複数宛先をまとめるためメッセージIDでグルーピング
  const msgIdGroup = _groupBy(flatMessages, 'message_id');
  return Object.entries(msgIdGroup).map(([id, msgGroup]) => {
    const msg = msgGroup[0];
    return {
      id,
      message: msg.message,
      timestamp: msg.message_time,
      from: {
        type: msg.link_type,
        name: msg.link_type === MESSAGE_TYPE_UPLINK ? msg.sender_nickname : msg.aircraftCode
      },
      ...(msg.link_type === MESSAGE_TYPE_DOWNLINK && {
        status: msg.status,
        delete_flag: msg.delete_flag
      }),
      ...(msg.link_type === MESSAGE_TYPE_UPLINK && {
        to: msgGroup.map(g => ({status: g.status, aircraft_code: g.aircraftCode, delete_flag: g.delete_flag}))
      }),
      ...(msg.link_type === MESSAGE_TYPE_UPLINK ? {
        upsertTime: maxBy(msgGroup, 'upsert_time').upsert_time
      } : {
        upsertTime: msg.upsert_time
      })
    };
  });
};

/**
 * 取得済みのメッセージ一覧(prevData)と新しく取得したメッセージ一覧(newData)をマージして取得.
 */
const mergeMessage = (prevData, newData) => {
  const messageList = deepmerge(prevData, newData, {
    arrayMerge: (target, source) => {
      const destination = target.slice();
      const dataIndex = new Map();
      target.forEach((item, idx) => {
        dataIndex.set(item.id, idx);
      });
      source.forEach((item) => {
        if (dataIndex.has(item.id)) {
          //既存メッセージの更新
          destination[dataIndex.get(item.id)] = deepmerge(destination[dataIndex.get(item.id)], item, {
            customMerge: key => {
              if (key === 'to') {
                return (toA, toB) => {
                  return deepmerge(toA, toB, {
                    arrayMerge: (toTarget, toSource) => {
                      const toDestination = toTarget.slice();
                      const toIndex = new Map();
                      toTarget.forEach((toItem, toIdx) => {
                        toIndex.set(toItem.aircraft_code, toIdx);
                      });
                      toSource.forEach(to => {
                        if (toIndex.has(to.aircraft_code)) {
                          toDestination[toIndex.get(to.aircraft_code)] = deepmerge(toTarget[toIndex.get(to.aircraft_code)], to);
                        } else {
                          //このルートはありえない
                          console.error(`message aircraft_code missing: ${item.id} - ${to.aircraft_code}`);
                        }
                      });
                      //delete_flag=trueのものを除去
                      return toDestination.filter(to => !to.delete_flag);
                    }
                  })
                }
              }
            }
          })
        } else {
          //新規メッセージ
          if (item.from.type === MESSAGE_TYPE_UPLINK) {
            // delete_flag=trueのものを除去
            item.to = item.to.filter(to => !to.delete_flag)
          }
          destination.push(item);
        }
      });

      return destination.filter(m => {
        //delete_flag=trueのものを除去
        if (m.from.type === MESSAGE_TYPE_DOWNLINK) {
          return !m.delete_flag
        } else {
          //toが0件ものを除去
          return m.to.length !== 0
        }
      });
    }
  });
  return _orderBy(messageList, 'timestamp', 'asc');
};

/**
 * 全メッセージの中で最新のupsertTime.
 */
const messageLatestUpsertTimeSelector = selector({
  key : 'messageLatestUpsertTimeSelector',
  get: ({get}) => {
    const messageList = get(messageListState);
    if (messageList.length === 0) {
      return null;
    }
    return maxBy(messageList, 'upsertTime').upsertTime;
  }
})

/**
 * メッセージ送信待ち件数取得.
 * 選択中（コミュボに表示中）の宛先のメッセージ送信待ち件数を取得する
 * 選択中の宛先がnullの場合、undefinedを返却する（無用なAPIの実行を防ぐ）
 * 選択中の宛先がallの場合、引数のallAircraftCodeListの結果を取得（selectorをrefreshすることができないので引数として渡す）
 */
export const messageAircraftWaitingCountQuery = selectorFamily({
  key: 'messageAircraftWaitingCountQuery',
  get: allAircraftCodeList => async ({get}) => {
    const selectedGroup = get(messageSelectedGroupState);
    if (!selectedGroup) {
      return undefined;
    }
    const aircraftCodeList = selectedGroup.isAll ? allAircraftCodeList : selectedGroup.aircraft;
    const apiUrl = process.env.REACT_APP_MESSAGE_CHECK_API_URL;
    const apiKey = process.env.REACT_APP_MESSAGE_CHECK_API_KEY;
    return axios.get(`${apiUrl}/queuecheck`, {
      params: {
        aircraft_code: aircraftCodeList.join(',')
      },
      headers: {
        'x-api-key': apiKey
      },
      errorHandle: {
        message: COMMUNICATE_ERROR_MESSAGE_QUEUECHECK
      }
    }).then(res => {
      return res.data;
    }).catch(e => {
      console.error('message destination failed.', e);
    });
  }
});

/**
 * 機体番号をキーとしたメッセージ送信待ち件数
 */
export const messageAircraftWaitingCountState = atom({
  key: 'messageAircraftWaitingCountState',
  default: {}
});

/**
 * 機体番号をキーとしたメッセージ送信待ち件数を設定する.
 * messageAircraftWaitingCountStateを引数でマージする
 */
export const setAircraftWaitingCountSelector = selector({
  key: 'setAircraftWaitingCountSelector',
  set: ({get, set}, count) => {
    const prevData = get(messageAircraftWaitingCountState);
    const newData = { ...prevData, ...count};
    set(messageAircraftWaitingCountState, newData);
  },
  get: () => {},
});
/**
 * 全体の最新メッセージ(送信・受信含めて最新）.
 */
const latestMessageSelector = selector({
  key: 'latestMessageSelector',
  get: ({get}) => {
    const list = get(messageListState);
    const latest = _orderBy(list, 'timestamp', 'desc')[0];
    return latest || {message: '', timestamp: ''};
  }
});

/**
 * 機体毎の最新メッセージ(受信).
 */
export const latestReceiveMessageFamily = atomFamily({
  key: 'latestReceiveMessageFamily',
  default: selectorFamily({
    key: 'latestReceiveMessageFamilySelector',
    get: aircraftCode => ({get}) => {
      const list = get(messageListState);
      const filtered = list.filter(m => {
        return m.from.name === aircraftCode;
      });
      const latest = _orderBy(filtered, 'timestamp', 'desc')[0];
      return latest || {message: '', timestamp: ''};
    }
  })
});

/**
 * 機体毎の最新メッセージ(送信).
 */
const latestSendMessageFamily = atomFamily({
  key: 'latestSendMessageFamily',
  default: selectorFamily({
    key: 'latestSendMessageFamilySelector',
    get: aircraftCode => ({get}) => {
      const list = get(messageListState);
      const filtered = list.filter(m => {
        return (m.from.type === MESSAGE_TYPE_UPLINK) && m.to.map(t=>t.aircraft_code).includes(aircraftCode);
      });
      const latest = _orderBy(filtered, 'timestamp', 'desc')[0];
      return latest || {message: '', timestamp: ''};
    }
  })
});

/**
 * 機体ごとの最新メッセージ取得(送信受信含めて最新).
 * 指定されたaircraftCodeの最新メッセージを取得.
 * aircraftCodeを配列で指定した場合、その中で最新のメッセージを取得.
 */
export const latestMessageWithAircraftCode = selectorFamily({
  key: 'latestMessageWithAircraftCode',
  get: aircraftCode => ({get}) => {
    const latest = [aircraftCode].flat().map(code => {
      const receive = get(latestReceiveMessageFamily(code));
      const send = get(latestSendMessageFamily(code));
      return receive.timestamp > send.timestamp ? receive : send;
    });
    if (latest.length === 1) {
      return latest[0];
    }
    return _orderBy(latest, 'timestamp', 'desc')[0];
  }
});

/**
 * ユーザIDを指定して全機体の最終既読メッセージIDを取得.
 */
const messageReadMarkState = atom({
  key: 'messageReadMarkState',
  default: selector({
    key: 'messageReadMarkStateSelector',
    get: ({get}) => {
      const user = get(userState);
      const uuid = user.userId;
      const aircraftIds = get(messageAircraftIds(user.group));
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      return axios.get(`${apiUrl}/uplink/read/get`, {
        params: {
          user_id: uuid,
          aircraft_code: aircraftIds.join(',')
        },
        headers: {
          'x-api-key': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_READ_GET
        }
      }).then(res => {
        return res.data.reduce((obj, marks) => {
          obj[marks.aircraft_code] = marks.read_message_id;
          return obj;
        }, {});
      }).catch(e => {
        console.error('message read mark failed.', e);
        return {};
      });
    }
  })
});

/**
 * 機体毎の最終既読メッセージID.
 */
const messageReadMarkSelector = selectorFamily({
  key: 'messageReadMarkSelector',
  get: aircraftCode => ({get}) => {
    const marked = get(messageReadMarkState)[aircraftCode];
    return marked || '';
  },
  set: (updateList) => ({get, set}) => {
    const old = get(messageReadMarkState);
    const newValue = {...old};
    updateList.forEach(v => {
      newValue[v.aircraft_code] = v.read_message_id;
    });
    set(messageReadMarkState, newValue);
  }
});

/**
 * 機体毎の未読メッセージ取得
 */
const messageUnreadListWithAircraftCode  = selectorFamily({
  key: 'messageUnreadListWithAircraftCode',
  get: ({aircraftCode, messageList}) => ({get}) => {
    const list = messageList ?? get(messageListState);
    const filtered = list.filter(m => {
      return m.from.name === aircraftCode && m.from.type === MESSAGE_TYPE_DOWNLINK;
    });
    const sorted = _orderBy(filtered, 'timestamp', 'asc');
    const readMark = get(messageReadMarkSelector(aircraftCode));
    const idx = sorted.findIndex(msg => msg.id === readMark);
    if (idx === -1) {
      return sorted;
    }
    return sorted.slice(idx + 1)
  }
});

/**
 * 機体毎のメッセージ未読数取得
 */
const messageUnreadWithAircraftCode = selectorFamily({
  key: 'messageUnreadWithAircraftCode',
  get: (aircraftCode) => ({get}) => {
    const messageList = get(messageUnreadListWithAircraftCode({aircraftCode}));
    return messageList.length;
  }
});

/**
 * 全体のメッセージ未読件数取得.
 */
export const messageTotalUnreadSelector = selector({
  key: 'messageTotalUnreadSelector',
  get: ({get}) => {
    const userGroup = get(userState).group;
    const aircraftList = get(messageAircraftListQuery(userGroup));
    return get(waitForAll(aircraftList.map(r => messageUnreadWithAircraftCode(r.aircraft_code))))
      .reduce((totalCount, count) => {
        totalCount += count
        return totalCount;
      }, 0);
  }
});

/**
 * グループ配下の機体のメッセージ未読件数取得
 */
const messageUnreadWithGroupSelectorFamily = selectorFamily({
  key: 'messageUnreadWithGroupSelectorFamily',
  get: aircraftCodeList => ({get}) => {
    return get(waitForAll(aircraftCodeList.map(aircraftCode => messageUnreadWithAircraftCode(aircraftCode))))
      .reduce((totalCount, count) => {
        totalCount += count
        return totalCount;
    }, 0);
  }
});

/**
 * 選択中のグループに属する機体一覧取得
 */
const aircraftListWithSelectedGroupSelector = selector({
  key: 'aircraftListWithSelectedGroupSelector',
  get: ({get}) => {
    //選択中のグループ取得
    const group = get(messageSelectedGroupState)
    let aircraftList;
    if (group.isAll) {
      const userGroup = get(userState).group;
      aircraftList = get(messageAircraftListQuery(userGroup)).map(r => r.aircraft_code);
    } else {
      aircraftList = group.aircraft;
    }
    return aircraftList;
  }
});

/**
 * グループ配下の機体の未読メッセージの中で一番古いメッセージID取得（初期スクロール位置に使用）.
 */
export const messageUnreadPositionInGroupSelector = selector({
  key: 'messageUnreadPositionInGroupSelector',
  get: ({get}) => {
    const aircraftCodeList = get(aircraftListWithSelectedGroupSelector);
    const target = aircraftCodeList.reduce((accumulator, aircraftCode) => {
      //機体の既読メッセージIDを取得
      const id = get(messageReadMarkSelector(aircraftCode));
      //未読メッセージ取得
      const msg = get(unreadMessageSelector({aircraftCode, id}));
      if (!msg) {
        return accumulator;
      }
      if (!accumulator.timestamp || accumulator.timestamp > msg.timestamp) {
        return msg;
      }
      return accumulator;
    }, {});
    return target.id;
  }
});

/**
 * 未読メッセージ（既読メッセージIDの次の受信メッセージ）取得.
 * 機体の受信メッセージがなければnull
 * 既読メッセージIDが指定されていなければ最古の受信メッセージ（既読情報がない）
 * 既読メッセージIDがリストに含まれなけれ最古の受信メッセージ（既読メッセージが古い）
 * 既読メッセージIDがその機体の最新受信メッセージであればnull
 */
const unreadMessageSelector = selectorFamily({
  key: 'unreadMessageSelector',
  get: ({aircraftCode,id}) => ({get}) => {
    const list = get(messageListState);
    const downLinkList = list.filter(m => m.from.type === MESSAGE_TYPE_DOWNLINK && m.from.name === aircraftCode);
    if (downLinkList.length === 0) {
      //受信メッセージがない
      return null;
    }
    if (!id) {
      //既読メッセージIDが指定されていない
      return downLinkList[0];
    }
    const index = downLinkList.findIndex(m => m.id === id);
    if (index === -1) {
      //受信メッセージのリストに既読メッセージIDが含まれない（既読メッセージが古い）
      return downLinkList[0];
    }
    return downLinkList[index + 1] || null;
  }
});

/**
 * 最終既読メッセージIDを更新.
 */
export const updateMessageReadMark = selector({
  key: 'updateMessageReadMark',
  set: ({get, set}) => {
    //選択中のグループの機体一覧取得
    const aircraftList = get(aircraftListWithSelectedGroupSelector);
    const updateTarget = []
    //グループ配下の機体に未読があるか判定
    aircraftList.forEach(aircraft => {
      const unreadCnt = get(messageUnreadWithAircraftCode(aircraft));
      if (unreadCnt > 0) {
        //機体の最新メッセージ取得
        const latest = get(latestReceiveMessageFamily(aircraft))
        //最終既読メッセージIDの更新対象に追加(APIは最後にまとめて更新)
        updateTarget.push({
          aircraft_code: aircraft,
          read_message_id: latest.id
        });
      }
    });
    if (!isEmpty(updateTarget)) {
      //メッセージ既読ステート更新(画面への反応を遅らせたくないのでAPI実行前にステートは更新してしまう)
      set(messageReadMarkSelector(updateTarget));

      const user = get(userState);
      const uuid = user.userId;
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      axios.put(`${apiUrl}/uplink/read/update`, {
        user_id: uuid,
        read_marks: updateTarget
      }, {
        headers: {
          'x-api-key': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_READ_UPDATE
        }
      }).then(() => {
        //特に何もしない
      }).catch(e => {
        console.log(e)
      })
    }
  },
  get: () => {}
});

/**
 * メッセージ宛先一覧
 */
export const messageAircraftListQuery = selectorFamily({
  key: 'messageSendListQuery',
  get: useGroupId => async () => {
    const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
    const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
    return axios.get(`${apiUrl}/links/message_destination`, {
      params: {
        usergroup_id: useGroupId
      },
      headers: {
        'x-api-key': apiKey
      },
      errorHandle: {
        message: COMMUNICATE_ERROR_MESSAGE_MESSAGE_DESTINATION
      }
    }).then(res => {
      return res.data;
    }).catch(e => {
      console.error('message destination failed.', e);
      throw e;
    });
  }
});

/**
 * メッセージ宛先一覧からaircraft_codeの配列を抽出
 */
export const messageAircraftIds = selectorFamily({
  key: 'messageAircraftIdsSelector',
  get: useGroupId => ({get}) => {
    const aircraftList = get(messageAircraftListQuery(useGroupId));
    return aircraftList.map(aircraft => aircraft.aircraft_code);
  }
});

/**
 * 選択中（コミュボに表示中）のグループ
 */
export const messageSelectedGroupState = atom({
  key: 'messageSelectedGroupState',
  default: null
});

/**
 * メッセージフィルタと宛先を共有するステート
 */
export const messageSelectedCopiIds = atom({
  key: 'messageSelectedCopiIds',
  default: selector({
    key: 'messageSelectedCopiIdsSelector',
    get: ({get}) => {
      const group = get(messageSelectedGroupState);
      if (group.isAll) {
        const userGroup = get(userState).group;
        return get(messageAircraftIds(userGroup));
      } else {
        return group.aircraft;
      }
    }
  })
});

/**
 * 宛先（とメッセージフィルタ）で選択中の機体番号一覧(messageSelectedCopiIdsからallを除外)
 */
export const messageSelectedAircraftCode = selector({
  key: 'messageSelectedAircraftCode',
  get: ({get}) => {
    const list = get(messageSelectedCopiIds);
    return list.filter(id => id !== 'all');
  }
});

/**
 * メッセージグループ一覧
 */
export const messageGroupListState = atom({
  key: 'messageGroupListState',
  default: selector({
    key: 'messageGroupListStateSelector',
    get: ({get}) => {
      const user = get(userState);
      const uuid = user.userId;
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      return axios.get(`${apiUrl}/uplink/messagegroup/list`, {
        params: {
          user_id: uuid
        },
        headers: {
          'x-api-key': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_MESSAGEGROUP_LIST,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }
      }).then(res => {
        return res.data.list;
      }).catch(e => {
        console.error('message group failed.', e);
        return [];
      });
    }
  })
});

/**
 * メッセージグループに表示する一覧(All + Group + 単機)
 */
export const messageGroupListSelector = selector({
  key: 'messageGroupListSelector',
  get: ({get}) => {
    const userGroup = get(userState).group;
    const msgGroups = get(messageGroupListState);
    const aircraftList = get(messageAircraftListQuery(userGroup));
    //全体最新メッセージ
    const allLatestMessage = get(latestMessageSelector);
    //全体未読件数
    const allUnread = get(messageTotalUnreadSelector);
    const groupMsgList = [{
      name: 'ALL',
      icon: groupIcon,
      latestMessage: allLatestMessage.message,
      timestamp: allLatestMessage.timestamp,
      unread: allUnread,
      isAll: true,
      type: 1,
    }];
    msgGroups.forEach(group => {
      const latestMessage = get(latestMessageWithAircraftCode(group.aircraft_code));
      const unread = get(messageUnreadWithGroupSelectorFamily(group.aircraft_code));
      groupMsgList.push({
        id: group.message_group_id,
        name: group.message_group_name,
        aircraft: group.aircraft_code,
        icon: groupIcon,
        latestMessage: latestMessage?.message ?? '',
        timestamp: latestMessage?.timestamp ?? '',
        unread: unread,
        type: 2,
      });
    });
    aircraftList.forEach(aircraft => {
      const latestMessage = get(latestMessageWithAircraftCode(aircraft.aircraft_code));
      const unread = get(messageUnreadWithAircraftCode(aircraft.aircraft_code));
      groupMsgList.push({
        name: aircraft.aircraft_code,
        aircraft: [aircraft.aircraft_code],
        icon: aircraft.aircraft_image || "",
        latestMessage: latestMessage?.message ?? '',
        timestamp: latestMessage?.timestamp ?? '',
        unread: unread,
        type: 3,
      });
    });

    return _orderBy(groupMsgList, ['timestamp', 'type'], ['desc', 'asc']);
  }
});

/**
 * メッセージ送信処理中、メセージ取消処理中の場合true.
 * 上記処理中の場合に一覧取得のポーリングが行われないようにするための制御フラグ
 */
export const messageProcessingState = atom({
  key: 'messageProcessingState',
  default: false
});

/**
 * メッセージリストのスクロール位置が最下部の場合true.
 * メッセージリストに新着メッセージの表示を行うかの判断に利用する
 */
export const messageScrollIsBottomState = atom({
  key: 'messageScrollIsBottomState',
  default: false
});

export const useMessage = () => {
  const userGroup = useRecoilValue(userState).group;
  const aircraftList = useRecoilValue(messageAircraftListQuery(userGroup));
  //メッセージ一覧更新
  const {execute} = useRecoilValue(messageListUpdater(userGroup));
  //着信音
  const {play} = useDownLinkSound();

  const sendMessage = useRecoilCallback(({snapshot, set}) => message => {
    set(messageProcessingState, true);
    const release = snapshot.retain();
    return new Promise(async (resolve, reject) => {
      const latestUpsertTime = await snapshot.getPromise(messageLatestUpsertTimeSelector);
      const user = snapshot.getLoadable(userState).contents;
      const userName = user.userName;
      const allAircraftList = await snapshot.getPromise(messageAircraftListQuery(user.group));
      const allAircraftListCode = allAircraftList.map(a => a.aircraft_code);
      const prevData = await snapshot.getPromise(messageListState);
      const waitCount = await snapshot.getPromise(messageAircraftWaitingCountState);
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      return axios.post(`${apiUrl}/uplink/register`, {
        aircraft_code_post: message.destination,
        aircraft_code_thread: allAircraftListCode,
        last_upsert_time: latestUpsertTime || '',
        account: userName,
        message: message.message,
        message_no: message.fixedPhraseNo,
        sender_nickname: message.nickname
      }, {
        headers: {
          'x-api-key': apiKey
        }
      }).then(async res => {
        const newData = formatMessage(res.data.threads);
        // 前回までのデータと今回のデータをマージ
        const mergeData =  mergeMessage(prevData, newData);
        // メッセージにアイコンを埋め込む
        const messageList = mergeData.map(msg => {
          const message = {...msg};
          if (msg.from.type === MESSAGE_TYPE_DOWNLINK && !msg.image) {
            const icon = aircraftList.find(aircraft => aircraft.aircraft_code === msg.from.name);
            message.image = icon?.aircraft_image ?? '';
          }
          return message;
        });
        set(messageListState, messageList);

        //未読件数取得
        if (newData.length > 0) {
          // 全機体の中でメッセージ受信音の設定がONになっている機体を取得
          const alertSetting = await snapshot.getPromise(coPiAlertState);
          const soundAircraftList = allAircraftListCode.filter(aircraftCode => {
            const alertRecord = alertSetting.find(alert=> aircraftCode === alert.aircraft_code);
            return alertRecord?.alertSwitch ?? false
          });

          //対象機体の未読件数を取得
          const unreadCount = snapshot.getLoadable(waitForAll(soundAircraftList.map(aircraftCode => messageUnreadListWithAircraftCode({aircraftCode, messageList: newData})))).contents
            .reduce((totalCount, unReadList) => {
              totalCount += unReadList.length
              return totalCount;
            }, 0);

          //着信音を鳴らす
          if (unreadCount > 0) {
            play()
          }
        }
        resolve()
      }).catch(e => {
        if (e.response?.status === 400) {
          const updateWaitCount = {
            ...waitCount
          };
          e.response.data?.aircraft_code?.forEach(aircraftCode => {
            updateWaitCount[aircraftCode]= 5 //送信disabledになる件数
          });
          set(messageAircraftWaitingCountState, updateWaitCount);
        }
        console.error('message send failed.', e);
        reject(e);
      }).finally(() => {
        set(messageProcessingState, false);
        release();
      });
    });
  });

  const cancelMessage = useRecoilCallback(({snapshot, set}) => message => {
    set(messageProcessingState, true);
    return new Promise(async (resolve, reject) => {
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      return axios.delete(`${apiUrl}/uplink/delete`, {
        params: {
          aircraft_code: message.destination.join(','),
          message_id: message.id,
        },
        headers: {
          'x-api-key': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_UPLINK_DELETE
        }
      }).then(async () => {
        /* 取消成功or失敗の結果は無視して、メッセージ一覧を再取得。
         * メッセージ一覧を再取得することで、削除成功してればdelete_flagがついて消える。
         * 削除失敗してれば送信待ち以外のステータスになっているはず。
         */
        const {unreadCount} = await execute();

        //着信音を鳴らす
        if (unreadCount > 0) {
          play()
        }
        resolve();
      }).catch(e => {
        console.error('message cancel failed.', e);
        reject(e);
      }).finally(() => {
        set(messageProcessingState, false);
      });
    });
  });

  const scrollToBottom = useRecoilCallback(({set}) => () => {
    //メッセージのスクロール位置を一番下にする
    const msgListElement = document.getElementById('communication-board-message-list').getElementsByClassName('message');
    if (msgListElement.length !== 0) {
      msgListElement[msgListElement.length - 1].scrollIntoView();
    }
    set(messageScrollIsBottomState, true);
    //新着メッセージは空にする
    set(newMessageListState, []);
  });

  return {
    sendMessage,
    cancelMessage,
    scrollToBottom
  }
};

export const useMessageGroup = () => {
  const addMessageGroup = useRecoilCallback(({snapshot, set})=> (group) => {
    return new Promise(async(resolve, reject) => {
      const user = await snapshot.getPromise(userState);
      const userId = user.userId;
      const oldList = await snapshot.getPromise(messageGroupListState);
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      return axios.post(`${apiUrl}/uplink/messagegroup/register`, {
        user_id: userId,
        group_name: group.name,
        aircraft_code: group.aircraft
      }, {
        headers: {
          'x-api-key': apiKey
        }
      }).then(res => {
        const idx = oldList.findIndex(target => {
          return target.message_group_id === res.data.Message_Group_id;
        });
        if (idx !== -1) return;
        const newList = [...oldList, {
          message_group_id: res.data.Message_Group_id,
          message_group_name: group.name,
          aircraft_code: group.aircraft
        }];
        set(messageGroupListState, newList);
        resolve()
      }).catch(e => {
        console.error('message group register failed.', e);
        reject(e);
      });
    });
  });

  const removeMessageGroup = useRecoilCallback(({snapshot, set})=> groupId => {
    return new Promise(async (resolve, reject) => {
      const oldList = await snapshot.getPromise(messageGroupListState);
      const apiUrl = process.env.REACT_APP_MESSAGE_API_URL;
      const apiKey = process.env.REACT_APP_MESSAGE_API_KEY;
      return axios.delete(`${apiUrl}/uplink/messagegroup/delete`, {
        params: {
          message_group_id: groupId
        },
        headers: {
          'x-api-key': apiKey
        },
      }).then(() => {
        const idx = oldList.findIndex(target => {
          return target.message_group_id === groupId;
        });
        if (idx === -1) return;
        const newList = [...oldList.slice(0, idx), ...oldList.slice(idx + 1)];
        set(messageGroupListState, newList);
        resolve()
      }).catch(e => {
        console.error('message group register failed.', e);
        reject(e);
      });
    });
  });
  return {
    addMessageGroup,
    removeMessageGroup
  }
};
